import { defined } from './helpers';
import * as Ease from './Easing';

const BALL_RADIUS = 10;
const ELEMENT_HEIGHT = 31;
const LAYER_BALL_HEIGHT = 32;
const LAYER_TEXT_HEIGHT = 12;
const LAYER_ALTITUDE_PADDING = 10;
const DRAW_PATH_LINE = false;
const DRAW_LAYER_DIVIDER = false;

// This is the maximum multiplier of the altitude gradient.
// E.g. If set to 10, the steepest part of the profile will be no more than
// 10 times steeper than the true grade of the track. Setting it to 1 will
// mean that the steepness you see in the profile is the true steepness.
const MAX_HEIGHT_STRETCH = 10.0;

export default class TerrainProfileManager {
  onSceneLoad(scene, features) {
    this.instance = scene.course ? new CourseProfile() : new TrackProfile();
    this.enableElements = features.terrain_profile_photos;
  }

  onComponentMounted(bgCanvas, fgCanvas, svgClipPath) {
    console.assert(this.instance);
    return this.instance.onComponentMounted(bgCanvas, fgCanvas, svgClipPath);
  }

  onPlayerPlaying(store) {
    this.store = store;
    if (this.instance) this.instance.onPlayerPlaying(store);
  }

  onResize(width, height) {
    if (this.instance) this.instance.onResize(width, height);
  }

  onScrubStart(percent) {
    if (this.instance) this.instance.onScrubStart(percent);
    this.store.dispatch({ type: 'PUSH_PAUSE' });
  }

  onScrubEnd(percent) {
    analytics.event({
      category: 'Navigation',
      action: 'Time Slider',
    });

    if (this.instance) this.instance.onScrubEnd(percent);
    this.store.dispatch({ type: 'POP_PAUSE' });
  }

  onScrub(percent) {
    if (this.instance) this.instance.onScrub(percent);
  }

  addElement(icon, trackId, time, callback) {
    if (!this.enableElements) {
      return {};
    }

    if (this.instance && this.instance.addElement) return this.instance.addElement(icon, trackId, time, callback);
    else return {};
  }

  update(state) {
    if (this.instance) this.instance.update(state);
  }

  render(state) {
    if (this.instance) this.instance.render(state);
  }
}

/***************************************************************************************************
 * TerrainProfile Base Class
 **************************************************************************************************/
class TerrainProfile {
  fgCanvas;
  bgCanvas;
  elements = [];
  _bg = {};

  onComponentMounted(bgCanvas, fgCanvas, svgClipPath) {
    this.bgCanvas = bgCanvas;
    this.fgCanvas = fgCanvas;
    this.svgClipPath = svgClipPath;
    this.container = fgCanvas.parentElement;
    this.maskCanvas = document.createElement('canvas');
    this.fgCtx = this.fgCanvas.getContext('2d');
    this.bgCtx = this.bgCanvas.getContext('2d');
    this.maskCtx = this.maskCanvas.getContext('2d');
    this.pixelRatio = window.devicePixelRatio || 1.0;
    this.canvasSize = {};
  }

  onPlayerPlaying(store) {
    this.store = store;
  }

  onResize(width, height) {
    // Determine correct canvas resolution
    this.canvasSize.x = width;
    this.canvasSize.y = height;

    // Support Retina displays
    this.fgCanvas.width = this.canvasSize.x * this.pixelRatio;
    this.fgCanvas.height = this.canvasSize.y * this.pixelRatio;
    this.bgCanvas.width = this.canvasSize.x * this.pixelRatio;
    this.bgCanvas.height = this.canvasSize.y * this.pixelRatio;
    this.maskCanvas.width = this.canvasSize.x;
    this.maskCanvas.height = this.canvasSize.y;

    this.fgCtx.scale(this.pixelRatio, this.pixelRatio);
    this.bgCtx.scale(this.pixelRatio, this.pixelRatio);
  }

  onScrubStart() {}

  onScrubEnd() {}

  onScrub() {}

  buildPath(canvasSize, track, activity, readyTime) {
    if (!track) return;

    const needsRebuild =
      track.dirty ||
      track !== this._bg.track ||
      canvasSize.x !== this._bg.width ||
      canvasSize.y !== this._bg.height ||
      readyTime !== this._bg.readyTime;

    this._bg.track = track;
    this._bg.readyTime = readyTime;
    this._bg.width = canvasSize.x;
    this._bg.height = canvasSize.y;

    if (needsRebuild && canvasSize.x > 0) {
      const heights = track.fetchChannel('height');
      const depths = track.hasChannel('depth') ? track.fetchChannel('depth') : undefined;
      const dists = track.fetchChannel('cumulative');

      this.heightMinMax = {};
      for (let i = 0; i < heights.length; i++) {
        if (!(heights[i] >= this.heightMinMax.min)) this.heightMinMax.min = heights[i];
        if (!(heights[i] <= this.heightMinMax.max)) this.heightMinMax.max = heights[i];
      }

      this.distanceMax = getMaxDistance(activity, track);

      const pathXs = [];
      const pathYs = [];
      for (let i = 0; i < track.length; i++) {
        if (!depths || !(depths[i] >= 9)) {
          const d = dists[i];
          const h = heights[i];
          pathXs.push(distanceToPx(d, this.distanceMax, canvasSize.x));
          pathYs.push(heightToPx(h, this.heightMinMax, this.distanceMax, canvasSize));
        }
      }

      // Render the altitude path into the alpha mask texture
      // @NOTE: This will be used to mask out the bg and fg canvases because
      //        Canvas' clipPath is too expensive and CSS clip path does not
      //        work with Safari. This is very fill heavy for the GPU.
      this.maskCtx.clearRect(0, 0, this.bgCanvas.width, this.bgCanvas.height);
      drawAltitudePath(this.maskCtx, this.canvasSize, pathXs, pathYs);

      // Fill the background canvas with the masked altitude path and distances
      this.bgCtx.clearRect(0, 0, this.bgCanvas.width, this.bgCanvas.height);
      this.bgCtx.fillStyle = 'rgba(255,255,255,0.3)';
      this.bgCtx.fillRect(0, 0, this.canvasSize.x, this.canvasSize.y);
      drawAlphaMask(this.bgCtx, this.maskCanvas);

      // Build an SVG to clip mouse events
      let pathStr = 'M ';
      for (let i = 0; i < pathXs.length; i++) {
        pathStr = `${pathStr + pathXs[i]} ${pathYs[i]} L `;
      }
      pathStr = `${pathStr + canvasSize.x} ${canvasSize.y} L  0 ${canvasSize.y}`;
      this.svgClipPath.setAttribute('d', pathStr);

      // Modify the clip region for the terrain profile to only show the loaded portion
      // @NOTE: This uses a CSS transition to animate smoothly
      if (activity && activity.cumulativeDistance) {
        const readyDist = track.sampleLinear({ time: readyTime }, { cumulative: 0 }).cumulative;
        const readyPercent = 100 * (readyDist / this.distanceMax);
        this.container.style.clipPath = `inset(0 ${100 - readyPercent}% 0 0)`;
      }
    }
  }
}

/***************************************************************************************************
 * Terrain Profile Types
 **************************************************************************************************/
class TrackProfile extends TerrainProfile {
  addElement(icon, trackId, time, callback) {
    const element = { icon, trackId, time, callback };
    this.elements.push(element);
    return element;
  }

  update(state) {
    if (!this.fgCanvas) {
      return;
    }

    // Resize our canvas when our element size changes
    const clientWidth = Math.round(this.fgCanvas.clientWidth);
    const clientHeight = Math.round(this.fgCanvas.clientHeight);
    if (clientWidth !== this.canvasSize.x || clientHeight !== this.canvasSize.y) {
      this.onResize(clientWidth, clientHeight);
    }

    // Rebuild our background path if the trail or size has changed
    const activeId = state.tracks.activeTrack;
    const activeTrack = state.tracks.tracks[activeId];
    const activeAct = state.scene.activities[activeId];
    const activeHead = state.playheads[activeId];
    const readyTime = state.clock.startTime + state.loading.sceneReadyTime;
    this.buildPath(this.canvasSize, activeTrack, activeAct, readyTime);

    // Compute stats for the active activity
    const activeXPos = activeHead && activeHead.ready ? activeHead.cumulative / this.distanceMax : 0.0;

    // Attempt to find positions for our elements
    for (let i = 0; i < this.elements.length; i++) {
      const elem = this.elements[i];

      // Elements are only shown if they belong to this track
      elem.active = elem.trackId === activeId;

      if (!defined(elem.distance)) {
        const result = { cumulative: 0.0, height: 0.0 };
        activeTrack.sampleLinear({ time: elem.time }, result);

        elem.distance = result.cumulative;
        elem.height = result.height;
      }

      elem.xPos = (elem.distance / this.distanceMax) * this.canvasSize.x;
      elem.yPos = heightToPx(elem.height, this.heightMinMax, this.distanceMax, this.canvasSize);

      // This element is "targeted" if the avatar line is close enough (and we're scrubbing)
      const targeted = isElementTargeted(elem.xPos / this.canvasSize.x, activeXPos) && this.scrubbing;
      if (targeted && !(elem.targetTime > 0)) elem.targetTime = Date.now();
      if (!targeted) elem.targetTime = null;

      if (!targeted && !(elem.exitTime > 0)) elem.exitTime = Date.now();
      if (targeted) elem.exitTime = null;
    }
  }

  onScrub(percent) {
    const state = this.store.getState();
    const activeTrack = state.tracks.tracks[state.tracks.activeTrack];

    // Find the time when the active track was at the target distance
    // @NOTE: If there is no active track (live with no activities), do nothing
    if (activeTrack) {
      const targetDistance = this.distanceMax * percent;
      const result = activeTrack.sampleLinear({ cumulative: targetDistance }, { time: undefined });
      if (defined(result.time)) {
        this.store.dispatch({ type: 'SET_TIME', time: result.time });
      }
    }
  }

  onScrubStart() {
    this.scrubbing = true;
  }

  onScrubEnd(seekPercent) {
    // If we've scrubbed to an element, find it
    const activeElements = this.elements.filter(e => e.active);
    const targetElement = activeElements.find(e => isElementTargeted(e.distance / this.distanceMax, seekPercent));

    // If scrubbed to an element, snap to its time
    if (targetElement) {
      this.store.dispatch({ type: 'SET_TIME', time: targetElement.time });
    }
    this.scrubbing = false;
  }

  render(state) {
    const activeId = state.tracks.activeTrack;
    const activeTrack = state.tracks.tracks[activeId];
    const activeHead = state.playheads[activeId];

    // Let's see if the profile track is ready...
    const trackReady = activeTrack && activeTrack.cumulative.last > 0;
    const canvasReady = this.fgCanvas && this.canvasSize.x > 0;
    if (!trackReady || !canvasReady) return;

    // Compute stats for the active activity
    let activeXPos = 0.0;
    if (activeHead && activeHead.ready) {
      const activeDist = activeHead.cumulative;
      activeXPos = (activeDist / this.distanceMax) * this.canvasSize.x;
    }

    const markers = [{ id: activeId, xPos: activeXPos, color: 'rgba(210,210,210,0.6)', active: true }];

    this.fgCtx.save();

    // Clear the whole canvas
    this.fgCtx.clearRect(0, 0, this.fgCanvas.width, this.fgCanvas.height);

    if (activeId) {
      const color = state.activities[activeId].color;
      drawCompletedGradient(this.fgCtx, this.canvasSize, color, activeXPos);
    }

    // The altitude path is still defined, draw a line along it
    if (DRAW_PATH_LINE) {
      this.fgCtx.lineWidth = 2;
      this.fgCtx.stroke();
    }

    drawDistanceLines(this.fgCtx, this.canvasSize, this.distanceMax);

    // Show all playheads that have a distance along the base track
    drawAvatarLines(this.fgCtx, this.canvasSize, markers);

    if (DRAW_LAYER_DIVIDER) {
      drawLayerDivider(this.fgCtx, this.canvasSize);
    }

    drawAlphaMask(this.fgCtx, this.maskCanvas);

    drawElements(this.fgCtx, this.elements);

    this.fgCtx.restore();
  }
}

class CourseProfile extends TerrainProfile {
  onPlayerPlaying(store) {
    super.onPlayerPlaying(store);

    // @TODO: This needs to work for WaypointCourse as well (brightday2)
    this.track = store.getState().tracks.tracks.__courseActivity;
  }

  update() {
    if (!this.fgCanvas) {
      return;
    }

    // Resize our canvas when our element size changes
    const clientWidth = Math.round(this.fgCanvas.clientWidth);
    const clientHeight = Math.round(this.fgCanvas.clientHeight);
    if (clientWidth !== this.canvasSize.x || clientHeight !== this.canvasSize.y) {
      this.onResize(clientWidth, clientHeight);
    }

    // Rebuild our background path if the trail or size has changed
    this.buildPath(this.canvasSize, this.track);
  }

  onScrub(percent) {
    // Find the time when the active track was at the target distance
    // @NOTE: If there is no active track (live with no activities), do nothing
    if (this.track) {
      const targetDistance = this.distanceMax * percent;
      const result = this.track.sampleLinear({ cumulative: targetDistance }, { time: undefined });
      if (defined(result.time)) {
        this.store.dispatch({ type: 'SET_TIME', time: result.time });
      }
    }
  }

  render(state) {
    // Let's see if the profile track is ready...
    const canvasReady = this.fgCanvas && this.canvasSize.x > 0;
    if (!canvasReady) return;

    // Compute stats for the active activity
    let activeXPos = 0.0;
    const activeId = state.tracks.activeTrack;
    const activeHead = state.playheads[activeId];
    if (activeHead && activeHead.ready) {
      const activeDist = activeHead.courseDistance;
      activeXPos = (activeDist / this.distanceMax) * this.canvasSize.x;
    }

    // Create a list of markers
    let markers = [];
    const tracks = Object.keys(state.playheads);
    const visibleTracks = tracks.filter(t => state.activities[t].show);

    markers = visibleTracks.map(headId => {
      const head = state.playheads[headId];
      const distFrac = head.courseDistance / this.distanceMax;
      return {
        id: headId,
        xPos: distFrac * this.canvasSize.x,
        color: state.activities[headId].color,
        active: headId === activeId,
      };
    });

    // Sort the markers so that the farther marker is on top
    markers = markers.sort((a, b) => a.xPos - b.xPos);

    this.fgCtx.save();

    // Clear the whole canvas
    this.fgCtx.clearRect(0, 0, this.fgCanvas.width, this.fgCanvas.height);

    if (activeId) {
      const color = state.activities[activeId].color;
      drawCompletedGradient(this.fgCtx, this.canvasSize, color, activeXPos);
    }

    // The altitude path is still defined, draw a line along it
    if (DRAW_PATH_LINE) {
      this.fgCtx.lineWidth = 2;
      this.fgCtx.stroke();
    }

    drawDistanceLines(this.fgCtx, this.canvasSize, this.distanceMax);

    // Show all playheads that have a distance along the base track
    drawAvatarLines(this.fgCtx, this.canvasSize, markers);

    if (DRAW_LAYER_DIVIDER) {
      drawLayerDivider(this.fgCtx, this.canvasSize);
    }

    drawAlphaMask(this.fgCtx, this.maskCanvas);

    this.fgCtx.restore();
  }
}

/***************************************************************************************************
 * Helpers
 **************************************************************************************************/
function drawLayerDivider(ctx, canvasSize) {
  ctx.strokeStyle = 'rgba(255,255,255,0.3)';
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 2]);
  ctx.beginPath();
  ctx.moveTo(0, canvasSize.y - LAYER_BALL_HEIGHT + 2);
  ctx.lineTo(canvasSize.x, canvasSize.y - LAYER_BALL_HEIGHT + 2);
  ctx.stroke();
}

function drawDistanceLines(ctx, canvasSize, maxDist) {
  const lineHeight = canvasSize.y - LAYER_BALL_HEIGHT;

  // Determine which multiple of 1km will be drawn so that 1km points always
  //   have a line, and the lines are about targetPx pixels apart
  const targetPx = BALL_RADIUS;
  const stepM = (targetPx / canvasSize.x) * maxDist;
  const mult =
    stepM > 1000.0
      ? 5.0 * Math.round(stepM / 5000.0) // Round to the nearest 5km increment
      : 1.0 / Math.round(1000.0 / stepM); // Round to nearest whole 1km divisor
  const step = 1000.0 * (mult || 1.0);

  ctx.fillStyle = 'rgba(255,255,255,0.2)';

  for (let d = 0.0; d < maxDist; d += step) {
    const x = distanceToPx(d, maxDist, canvasSize.x);
    ctx.fillRect(x, 0, 1, lineHeight);
  }
}

function drawCompletedGradient(ctx, canvasSize, color, xPos) {
  const stopColor = color;
  const startColor = color !== 'rgb(255, 51, 51)' ? 'rgb(18,18,33)' : 'rgb(33,32,60)';

  ctx.globalAlpha = 0.9;
  const gradient = ctx.createLinearGradient(0, 0, xPos, 0);
  gradient.addColorStop(0, startColor);
  gradient.addColorStop(1, stopColor);

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, xPos, canvasSize.y);
  ctx.globalAlpha = 1.0;
}

function drawAvatarLines(ctx, sizePx, markers) {
  const size = [4, LAYER_TEXT_HEIGHT];
  const yPosPxc = toNearestPixelCenter(sizePx.y - LAYER_BALL_HEIGHT - size[1] * 0.5);

  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.shadowBlur = 0;

  ctx.lineJoin = 'miter';
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgb(255, 255, 255)';

  for (let i = 0; i < markers.length; i++) {
    const xPosPxc = toNearestPixelCenter(markers[i].xPos);
    const coords = [xPosPxc - size[0] * 0.5, yPosPxc - size[1] * 0.5];
    ctx.fillStyle = markers[i].color;
    ctx.fillRect(coords[0], coords[1], size[0], size[1]);
    ctx.strokeRect(coords[0], coords[1], size[0], size[1]);
  }

  // Draw the inactive lines
  let activeIdx;
  ctx.beginPath();
  for (let i = 0; i < markers.length; i++) {
    if (markers[i].active) activeIdx = i;
    else {
      const xPosPxc = toNearestPixelCenter(markers[i].xPos);
      ctx.moveTo(xPosPxc, yPosPxc - size[1] * 0.5);
      ctx.lineTo(xPosPxc, 0);
    }
  }
  ctx.stroke();

  // Draw the active line
  if (activeIdx !== undefined) {
    const xPosPxc = toNearestPixelCenter(markers[activeIdx].xPos);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(xPosPxc, yPosPxc - size[1] * 0.5);
    ctx.lineTo(xPosPxc, 0);
    ctx.stroke();
  }
}

function drawAltitudePath(ctx, sizePx, xs = [], ys = []) {
  ctx.fillStyle = 'white';
  ctx.lineJoin = 'round';

  ctx.beginPath();

  for (let i = 0; i < xs.length; i++) {
    ctx.lineTo(xs[i], ys[i]);
  }

  // Close the path around the bottom of the canvas, staying outside the edges
  ctx.lineTo(sizePx.x + 1, ys[ys.length - 1]);
  ctx.lineTo(sizePx.x + 1, sizePx.y + 1);
  ctx.lineTo(-1, sizePx.y + 1);
  ctx.lineTo(-1, ys[0]);
  ctx.closePath();

  ctx.fill();
}

function drawAlphaMask(ctx, maskImg) {
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(maskImg, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
}

function drawElements(ctx, elements) {
  let ballRad, iconRad, offsetY;
  const kMaxBallRad = 14;
  const kMaxYOffset = 3;
  const kMaxIconRad = 9;

  for (let i = 0; i < elements.length; i++) {
    const e = elements[i];

    if (e.targetTime) {
      // Transition in
      const t = (Date.now() - e.targetTime) / 800;
      ballRad = Ease.outElastic(t) * kMaxBallRad;
      iconRad = Ease.outCubic(t * 2) * kMaxIconRad;
      offsetY = Ease.outElastic(t) * kMaxYOffset;
    } else {
      // Transition out
      const t = e.exitTime > 0 ? 1.0 - (Date.now() - e.exitTime) / 300 : 0.0;
      ballRad = Ease.inCubic(t) * kMaxBallRad;
      iconRad = Ease.inCubic(t) * kMaxIconRad;
      offsetY = Ease.inCubic(t) * kMaxYOffset;
    }

    const ballCenterY = e.yPos - ballRad - offsetY;

    // Background Circle
    ctx.fillStyle = 'rgba(250,250,250,0.8)';
    ctx.beginPath();
    ctx.arc(e.xPos, ballCenterY, ballRad, 0, 2 * Math.PI);
    ctx.fill();

    // Down line
    ctx.fillRect(e.xPos - 1, ballCenterY + ballRad, 2, kMaxBallRad + kMaxYOffset);

    // Icon
    ctx.drawImage(e.icon, e.xPos - iconRad, ballCenterY - iconRad, iconRad * 2, iconRad * 2);
  }
}

function distanceToPx(dist, maxDist, widthPx) {
  return (dist / maxDist) * widthPx;
}

function heightToPx(height, heightMinMax, maxDist, sizePx) {
  const padding = LAYER_ALTITUDE_PADDING;
  const rangePx = sizePx.y - LAYER_BALL_HEIGHT - LAYER_TEXT_HEIGHT - padding - ELEMENT_HEIGHT;
  const mToPx = sizePx.x / maxDist;
  const heightRange = heightMinMax.max - heightMinMax.min;
  const stretch = Math.min(rangePx / (heightRange * mToPx), MAX_HEIGHT_STRETCH);

  const gain = height - heightMinMax.min;
  const gainPx = gain * mToPx;

  return rangePx - gainPx * stretch + ELEMENT_HEIGHT;
}

function toNearestPixelCenter(x) {
  // Canvas pixel centers are at (0.5, 0.5)
  // E.g, 4.0-4.99 => 4.5, 5.0 - 5.99 => 5.5
  const pixelRatio = window.devicePixelRatio || 1.0;
  return (Math.floor(x * pixelRatio) + 0.5) / pixelRatio;
}

function getMaxDistance(activity, track) {
  // If we know the absolute distance of the track, use it.
  // Otherwise (i.e. live activities) just use the length of what we've loaded so far
  return activity && activity.cumulativeDistance ? activity.cumulativeDistance : track.cumulative.last;
}

function isElementTargeted(elementPos, scrubPos) {
  const kTargetedThreshold = 0.01;
  return Math.abs(elementPos - scrubPos) < kTargetedThreshold;
}
